import { isImporterReconciliationQueue } from "@common/helpers/financialDocument";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FILE_TYPE_EXTENSIONS } from "@common/constants/financial-documents";
import {
    getImportStatus,
    getReconciliationStatus,
} from "@components/documents/lib";
import {
    IMPORTER_FILE_TYPES,
    IMPORTER_FILE_TYPES_NAMES,
    RECONCILIATION_FILE_TYPES,
    RECONCILIATION_FILE_TYPES_NAMES,
} from "./constants";

interface ImporterDocumentEligibilityInput {
    financialDocument: FinancialDocumentListItem;
    importerQueue: string;
}

interface ImporterDocumentEligibility {
    isSupportedFileType: boolean;
    isAlreadyAdded: boolean;
    isAlreadyImported: boolean;
    isAlreadyReconciled: boolean;
    isDisabled: boolean;
    disabledTooltip?: string;
}

export function getImporterDocumentEligibility({
    financialDocument,
    importerQueue,
}: ImporterDocumentEligibilityInput): ImporterDocumentEligibility {
    const isReconciliation = isImporterReconciliationQueue(importerQueue);
    const fileTypes = isReconciliation
        ? RECONCILIATION_FILE_TYPES
        : IMPORTER_FILE_TYPES;
    const isSupportedFileType = fileTypes.some((fileType) =>
        financialDocument.fileName
            .toLowerCase()
            .endsWith(FILE_TYPE_EXTENSIONS[fileType]),
    );
    const isAlreadyAdded = financialDocument.importerQueue === importerQueue;
    const isAlreadyImported = getImportStatus(financialDocument) === "done";
    const isAlreadyReconciled =
        getReconciliationStatus(financialDocument) === "done";

    let disabledTooltip: string | undefined;
    if (isAlreadyReconciled) {
        disabledTooltip = "Document has already been reconciled";
    } else if (isAlreadyImported && !isReconciliation) {
        disabledTooltip = "Document has already been imported";
    } else if (isAlreadyAdded) {
        disabledTooltip = "Document is already in the importer";
    } else if (!isSupportedFileType) {
        disabledTooltip = isReconciliation
            ? `Only ${RECONCILIATION_FILE_TYPES_NAMES} are supported`
            : `Only ${IMPORTER_FILE_TYPES_NAMES} are supported`;
    }

    const isDisabled =
        !isSupportedFileType ||
        isAlreadyAdded ||
        isAlreadyReconciled ||
        (isAlreadyImported && !isReconciliation);

    return {
        isSupportedFileType,
        isAlreadyAdded,
        isAlreadyImported,
        isAlreadyReconciled,
        isDisabled,
        disabledTooltip,
    };
}
